// File: src/pages/host/HostDesktopPortal.jsx
import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { LuCrown, LuLogOut, LuArrowLeft } from 'react-icons/lu';
import HostDocsGateModal from '../../components/host/modals/HostDocsGateModal';
import { ReactComponent as TribeLogo } from '../../assets/TribeLogo.svg';
import { AuthContext } from '../../context/AuthContext';

// Decorative preview only. Event signup will be added when the marketplace launches.
function MarketplacePreview() {
  return (
    <div className="host-preview-background" aria-hidden="true">
      <div className="host-preview-window">
        <div className="host-preview-toolbar">
          <div className="host-preview-tabs">
            <span>Available events</span>
            <span>My events</span>
          </div>
          <div className="host-preview-avatar" />
        </div>
        <div className="host-preview-body">
          <div className="host-preview-filters">
            <div className="host-preview-search"><span /></div>
            <div className="host-preview-filter" />
            <div className="host-preview-filter" />
          </div>
          <div className="host-preview-grid">
            {[0, 1, 2].map((card) => (
              <div className="host-preview-card" key={card}>
                <div className="host-preview-cover">
                  <div className="host-preview-date"><span /><span /></div>
                </div>
                <div className="host-preview-card-copy">
                  <div className="host-preview-line host-preview-line-title" />
                  <div className="host-preview-line host-preview-line-meta" />
                  <div className="host-preview-card-bottom">
                    <div className="host-preview-line host-preview-line-pay" />
                    <div className="host-preview-signup" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="host-preview-upcoming">
            <span>Upcoming hosting</span>
            {[0, 1].map((row) => (
              <div className="host-preview-row" key={row}>
                <div className="host-preview-thumbnail" />
                <div className="host-preview-row-copy">
                  <div className="host-preview-line host-preview-line-title" />
                  <div className="host-preview-line host-preview-line-meta" />
                </div>
                <div className="host-preview-row-action" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HostDesktopPortal() {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const token = useMemo(() => (
    localStorage.getItem('accessToken') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('token') ||
    ''
  ), []);
  const [docsGateOpen, setDocsGateOpen] = useState(false);
  const [docsRequiredVersion, setDocsRequiredVersion] = useState('');

  useEffect(() => {
    if (currentUser === undefined) return;
    if (!currentUser?.isHost) {
      navigate('/events', { replace: true });
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!currentUser?.isHost || !token) return;
    let active = true;

    async function checkDocs() {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/host/docs/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!active) return;
        setDocsRequiredVersion(String(res.data?.requiredVersion || ''));
        setDocsGateOpen(!!res.data?.isHost && !res.data?.accepted);
      } catch (err) {
        if (active) {
          console.error('[HostDesktopPortal] docs status check failed', err);
        }
      }
    }

    checkDocs();
    return () => { active = false; };
  }, [currentUser, token]);

  function handleLogout() {
    try {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('authToken');
      localStorage.removeItem('token');
    } catch {}
    navigate('/events', { replace: true });
  }

  if (currentUser === undefined) {
    return <div role="status" style={{ padding: 32, color: '#777' }}>Loading…</div>;
  }
  if (!currentUser?.isHost) return null;

  const fullName = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim()
    || currentUser.email || 'Host';
  const avatarUrl = currentUser.profilePicUrl || '/default-avatar.png';

  return (
    <div className="host-desktop-portal">
      <header className="host-desktop-header">
        <div className="host-desktop-logo-row">
          <TribeLogo className="host-desktop-logo" aria-label="Tribe" />
        </div>
        <nav className="host-desktop-nav" aria-label="Host portal navigation">
          <div className="host-desktop-brand">
            <button
              className="host-desktop-back"
              type="button"
              onClick={() => navigate('/portal')}
              aria-label="Back to portal selection"
              title="Back"
            >
              <LuArrowLeft size={18} strokeWidth={1.8} aria-hidden="true" />
              <span>Back</span>
            </button>
          </div>
          <TribeLogo className="host-desktop-logo host-desktop-nav-logo" aria-label="Tribe" />
          <span className="host-desktop-title">HOST DASHBOARD</span>
          <div className="host-desktop-account">
            <div className="host-desktop-profile" title={fullName}>
              <img
                src={avatarUrl}
                alt=""
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = '/default-avatar.png';
                }}
              />
              <span>{fullName}</span>
            </div>
            <span className="host-desktop-account-divider" aria-hidden="true" />
            <button className="host-desktop-logout" type="button" onClick={handleLogout}>
              <LuLogOut size={15} strokeWidth={1.8} aria-hidden="true" />
              <span>Log out</span>
            </button>
          </div>
        </nav>
      </header>
      <main className="host-marketplace-landing">
      <style>{`
        .host-desktop-portal {
          min-height: 100vh;
          background: #fff;
          color: #111;
          font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
        }
        .host-desktop-portal .host-desktop-header {
          width: 100%;
          background: #fff;
          border-bottom: 1px solid #eeeeee;
        }
        .host-desktop-portal .host-desktop-nav {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
          align-items: center;
          gap: 24px;
          width: 100%;
          max-width: 1200px;
          min-height: 80px;
          margin: 0 auto;
          padding: 16px 32px;
          box-sizing: border-box;
        }
        .host-desktop-portal .host-desktop-brand,
        .host-desktop-portal .host-desktop-account {
          display: flex;
          align-items: center;
          min-width: 0;
        }
        .host-desktop-portal .host-desktop-brand { gap: 12px; }
        .host-desktop-portal .host-desktop-account { gap: 20px; justify-content: flex-end; }
        .host-desktop-portal .host-desktop-back {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          gap: 8px;
          min-height: 40px;
          padding: 0;
          border: 0;
          background: transparent;
          color: #666;
          font: inherit;
          font-size: 14px;
          font-weight: 400;
          cursor: pointer;
        }
        .host-desktop-portal .host-desktop-logo {
          display: block;
          width: 90px;
          height: 34px;
        }
        .host-desktop-portal .host-desktop-logo :is(path,circle,rect,polygon,polyline,ellipse) {
          fill: #050505;
        }
        .host-desktop-portal .host-desktop-logo [fill="none"] { fill: none; }
        .host-desktop-portal .host-desktop-account-divider {
          width: 1px;
          height: 20px;
          flex-shrink: 0;
          background: #e9e9e9;
        }
        .host-desktop-portal .host-desktop-title {
          display: none;
          justify-self: center;
          font-size: 15px;
          font-weight: 900;
          color: #000;
          white-space: nowrap;
        }
        .host-desktop-portal .host-desktop-profile {
          display: flex;
          align-items: center;
          gap: 9px;
          min-width: 0;
          font-size: 12px;
          font-weight: 500;
          color: #555;
        }
        .host-desktop-portal .host-desktop-profile img {
          width: 29px;
          height: 29px;
          border: 1px solid #eee;
          border-radius: 50%;
          object-fit: cover;
          flex-shrink: 0;
        }
        .host-desktop-portal .host-desktop-profile span {
          max-width: 150px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .host-desktop-portal .host-desktop-logout {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 44px;
          padding: 0;
          border: 0;
          background: transparent;
          color: #777;
          font: inherit;
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
          cursor: pointer;
        }
        .host-desktop-portal .host-desktop-logout:hover { color: #111; }
        .host-desktop-portal button:focus-visible {
          outline: 2px solid #777;
          outline-offset: 4px;
          border-radius: 4px;
        }
        @media (max-width: 600px) {
          .host-desktop-portal .host-desktop-nav { min-height: 72px; padding: 12px 20px; gap: 16px; }
          .host-desktop-portal .host-desktop-brand { gap: 10px; }
          .host-desktop-portal .host-desktop-account { gap: 12px; }
          .host-desktop-portal .host-desktop-profile span,
          .host-desktop-portal .host-desktop-account-divider { display: none; }
        }
        @media (max-width: 760px) {
          .host-desktop-portal .host-desktop-profile span { display: none; }
          .host-desktop-portal .host-desktop-title { font-size: 12px; }
        }
        @media (max-width: 600px) {
          .host-desktop-portal .host-desktop-title { display: none; }
        }
        .host-desktop-portal .host-desktop-logo-row { display: none; }
        @media (min-width: 761px) {
          .host-desktop-portal .host-desktop-logo-row {
            display: flex;
            align-items: flex-end;
            justify-content: center;
            height: 48px;
          }
          .host-desktop-portal .host-desktop-logo-row .host-desktop-logo {
            width: 72px;
            height: auto;
          }
          .host-desktop-portal .host-desktop-nav {
            min-height: 54px;
            padding-top: 0;
            padding-bottom: 0;
          }
          .host-desktop-portal .host-desktop-nav-logo { display: none; }
          .host-desktop-portal .host-desktop-title {
            display: block;
            line-height: 1;
            letter-spacing: .02em;
          }
        }
        .host-marketplace-landing {
          position: relative;
          isolation: isolate;
          display: grid;
          place-items: center;
          min-height: max(640px, calc(100vh - 100px));
          overflow: hidden;
          padding: 64px 24px;
          box-sizing: border-box;
          background: #fff;
          color: #111;
          font-family: inherit;
          text-align: center;
        }
        .host-marketplace-landing *,
        .host-marketplace-landing *::before,
        .host-marketplace-landing *::after { box-sizing: border-box; }
        .host-marketplace-landing .host-preview-background {
          position: absolute;
          z-index: -2;
          inset: 48px 24px;
          display: flex;
          justify-content: center;
          pointer-events: none;
          user-select: none;
          opacity: .65;
          -webkit-mask-image: linear-gradient(to bottom, transparent, #000 12%, #000 72%, transparent);
          mask-image: linear-gradient(to bottom, transparent, #000 12%, #000 72%, transparent);
        }
        .host-marketplace-landing .host-preview-window {
          width: 100%;
          max-width: 1000px;
          min-width: 700px;
          border: 1px solid #eee;
          border-radius: 18px;
          background: #fff;
          text-align: left;
          overflow: hidden;
        }
        .host-marketplace-landing .host-preview-toolbar {
          height: 66px;
          padding: 0 30px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid #eee;
        }
        .host-marketplace-landing .host-preview-tabs {
          display: flex;
          gap: 28px;
          font-size: 13px;
          font-weight: 700;
          color: #c3c3c3;
        }
        .host-marketplace-landing .host-preview-avatar {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          background: #f0f0f0;
        }
        .host-marketplace-landing .host-preview-body { padding: 26px 30px; }
        .host-marketplace-landing .host-preview-filters {
          display: flex;
          gap: 12px;
          margin-bottom: 24px;
        }
        .host-marketplace-landing .host-preview-search {
          width: 250px;
          height: 34px;
          border: 1px solid #ededed;
          border-radius: 9px;
          display: flex;
          align-items: center;
          padding: 0 12px;
        }
        .host-marketplace-landing .host-preview-search span {
          width: 100px;
          height: 5px;
          background: #eee;
          border-radius: 8px;
        }
        .host-marketplace-landing .host-preview-filter {
          width: 85px;
          height: 34px;
          border: 1px solid #ededed;
          border-radius: 999px;
        }
        .host-marketplace-landing .host-preview-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 20px;
        }
        .host-marketplace-landing .host-preview-card {
          border: 1px solid #eaeaea;
          border-radius: 12px;
          overflow: hidden;
        }
        .host-marketplace-landing .host-preview-cover {
          height: 126px;
          background: #f2f2f2;
          padding: 12px;
        }
        .host-marketplace-landing .host-preview-date {
          width: 32px;
          height: 36px;
          border-radius: 7px;
          background: #fff;
          display: grid;
          place-content: center;
          gap: 5px;
        }
        .host-marketplace-landing .host-preview-date span {
          width: 15px;
          height: 4px;
          border-radius: 4px;
          background: #e9e9e9;
        }
        .host-marketplace-landing .host-preview-card-copy { padding: 17px; }
        .host-marketplace-landing .host-preview-line {
          height: 6px;
          background: #eaeaea;
          border-radius: 8px;
        }
        .host-marketplace-landing .host-preview-line-title { width: 68%; height: 8px; }
        .host-marketplace-landing .host-preview-line-meta { width: 45%; margin-top: 10px; }
        .host-marketplace-landing .host-preview-line-pay { width: 42px; }
        .host-marketplace-landing .host-preview-card-bottom {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 25px;
        }
        .host-marketplace-landing .host-preview-signup {
          width: 70px;
          height: 25px;
          background: #ededed;
          border-radius: 999px;
        }
        .host-marketplace-landing .host-preview-upcoming {
          margin-top: 30px;
          color: #c3c3c3;
          font-size: 12px;
          font-weight: 700;
        }
        .host-marketplace-landing .host-preview-row {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 15px 0;
          border-bottom: 1px solid #eee;
        }
        .host-marketplace-landing .host-preview-thumbnail {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: #f0f0f0;
        }
        .host-marketplace-landing .host-preview-row-copy { flex: 1; max-width: 240px; }
        .host-marketplace-landing .host-preview-row-action {
          width: 65px;
          height: 25px;
          margin-left: auto;
          border-radius: 999px;
          border: 1px solid #eaeaea;
        }
        .host-marketplace-landing .host-marketplace-halo {
          position: absolute;
          z-index: -1;
          inset: 0;
          pointer-events: none;
          background: radial-gradient(ellipse 440px 290px at center, #fff 30%, rgba(255,255,255,.97) 49%, rgba(255,255,255,.75) 69%, transparent 100%);
        }
        .host-marketplace-landing .host-marketplace-message { width: 100%; max-width: 350px; }
        .host-marketplace-landing .host-marketplace-icon {
          display: grid;
          place-items: center;
          width: 76px;
          height: 76px;
          margin: 0 auto 24px;
          border-radius: 24px;
          background: #f3f3f3;
          color: #b9b9b9;
        }
        .host-marketplace-landing .host-marketplace-label {
          margin: 0 0 10px;
          color: #999;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: .09em;
          text-transform: uppercase;
        }
        .host-marketplace-landing h1 {
          margin: 0 0 12px;
          font-family: inherit;
          font-size: 28px;
          font-weight: 900;
          line-height: 1.2;
          letter-spacing: -.035em;
          color: #111;
        }
        .host-marketplace-landing .host-marketplace-description {
          margin: 0;
          font-size: 15px;
          line-height: 1.65;
          color: #777;
        }
        @media (max-width: 580px) {
          .host-marketplace-landing { min-height: 70vh; padding: 80px 24px; }
          .host-marketplace-landing .host-preview-background { inset: 36px -190px; opacity: .45; }
          .host-marketplace-landing .host-marketplace-halo {
            background: radial-gradient(ellipse 260px 250px at center, #fff 35%, rgba(255,255,255,.97) 58%, transparent 100%);
          }
          .host-marketplace-landing h1 { font-size: 24px; }
          .host-marketplace-landing .host-marketplace-description { font-size: 14px; }
        }
      `}</style>
      <MarketplacePreview />
      <div className="host-marketplace-halo" aria-hidden="true" />
      <div className="host-marketplace-message">
        <div className="host-marketplace-icon" aria-hidden="true">
          <LuCrown size={34} strokeWidth={1.5} />
        </div>
        <p className="host-marketplace-label">Coming soon</p>
        <h1>Host marketplace</h1>
        <p className="host-marketplace-description">
          Browse upcoming Tribe events and sign up to host, all in one place.
        </p>
      </div>
      </main>
      <HostDocsGateModal
        open={docsGateOpen}
        requiredVersion={docsRequiredVersion}
        onAccepted={() => setDocsGateOpen(false)}
      />
    </div>
  );
}
